import React from 'react'
import { Box, Typography, TypographyVariant } from '@mui/material'

interface DecorativeTitleProps {
    title: string
    variant?: TypographyVariant
    component?: React.ElementType
    color?: string
    customFontSize?: string | object
    lineWidth?: object
    layout?: 'row' | 'column'
}

const DecorativeTitle: React.FC<DecorativeTitleProps> = ({
    title,
    variant = 'h2',
    component = 'h1',
    color,
    customFontSize,
    lineWidth = { xs: 60, md: 100 },
    layout = 'row'
}) => {
    // Elegant Double Line CSS Component
    const GoldLine = () => (
        <Box
            className="no-print" // Hidden in print mode to save space
            sx={{
                width: lineWidth,
                height: { xs: 4, md: 6 },
                borderTop: (theme) => `2px solid ${theme.palette.mode === 'dark' ? '#fcd34d' : '#b45309'}`, // Thick top
                borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#fcd34d' : '#b45309'}`, // Thin bottom
                opacity: 0.8,
                flexShrink: 0
            }}
        />
    )

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: layout,
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 2, md: 3 },
                width: '100%',
                maxWidth: '100%',
                py: 1
            }}
        >
            {/* Top/Left Line */}
            <GoldLine />

            {/* Title */}
            <Typography
                variant={variant}
                component={component}
                align="center"
                sx={{
                    fontWeight: 700,
                    fontFamily: '"Noto Serif", serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    lineHeight: 1.2,
                    fontSize: customFontSize || 'clamp(1.5rem, 5vw, 5rem)',
                    color: color || ((theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#b45309'),
                    background: (theme) => theme.palette.mode === 'dark'
                        ? 'linear-gradient(to right, #fcd34d, #f59e0b)'
                        : 'linear-gradient(to right, #d97706, #b45309)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    whiteSpace: layout === 'row' ? 'nowrap' : 'normal', // Nowrap for row (Hero), Normal for col (Detail)
                    wordBreak: layout === 'row' ? 'normal' : 'break-word',
                }}
            >
                {title}
            </Typography>

            {/* Bottom/Right Line */}
            <GoldLine />
        </Box>
    )
}

export default DecorativeTitle
